//ResultsFS.tsx ==============================================================
import "../Util/Prototypes";

import * as CheckboxSale from "../Sales Search/CheckboxSale";
import * as Custom from "../Custom";
import * as Default from "../Default";
import * as DrawerMenu from "../Menus/DrawerMenu";
import * as EditValuesIBM from "../IBM/EditValuesIBM";
import * as GetSearchResults from "../Search/GetSearchResults";
import * as GraLay from "../Util/GraLay";
import * as MainMap from "../MainMap";
import * as SalesSearch from "../Sales Search/SalesSearch";
import * as Search from "../Search/Search";
import * as SelectionMode from "../Search/SelectionMode";
import * as Settings from "../Settings";
import * as Util from "../Util/Util";
import * as Util_App from "../Util/Util_App";

import { Accordion, AccordionDetails, AccordionSummary, Button, Icon, IconButton, Paper, Tooltip, Typography } from "@mui/material";

import Extent from "@arcgis/core/geometry/Extent";
import Graphic from "@arcgis/core/Graphic";

import React from "react";

export interface iResult {
   toolName: string;  // Identify, Search Results, Sales Results, Data Table
   layerName: string; // layer name.
   fs: __esri.FeatureSet;            // Resulting featureSet array.
   IDs?: string[] | number[]; // Object id's or parcel id's, etc.
   begRec?: number;   // Offset index into the results array.
   numRec?: number;   // Number of records to display in left panel.
   layerNameVisible?: boolean; //True will show the layer name heading in the results.
   append: boolean;   //True if scrolling more records into the result.
}

const keyModule: string = "ResultsFS"; //Module name for unique key.
let selectButtonVisible: boolean;
let salesCheckboxVisible: boolean; //For sales search.
let keyLayer: string;
let keyFeature: string;
let keyRow: string;
let key: string;
let jsxMain: JSX.Element[];
let displayFields: string[];

interface iProps {
   results: iResult[];
}

//Render function.
export function Render(props: iProps) {
   //Util.logMsg("Top of SelectionMode Render");

   //Get expansion panels.
   function getExpansionPanel(result: iResult, ii: number): JSX.Element[] {
      let i: number;
      let feature: Graphic;

      const layerName: string = result.layerName;
      const fs = result.fs;

      let begRec: number = result.begRec;
      const endRec: number = begRec + result.numRec - 1;

      if (endRec > result.fs.features.length) {
         Util.errMsg("Query did not return enough features for map image layer");
         return;
      }

      if (result.append) {
         begRec += jsxMain.length;
      } else {
         jsxMain = []; //Clear if not scrolling.
      }

      let expanded: boolean = false;
      if (props.results.length === 1 && result.numRec === 1) {
         expanded = true;
      }

      //Compute height of expansion panel summary.
      const layerSpec: Settings.iLayer = Util_App.getLayerSpec(layerName);
      let summaryHeight: number = 18 + (layerSpec.headingFields.length * 20);

      if (layerName === Settings.parcelLayerName && Settings.security.accessLevel === 10) {
         summaryHeight += 100;
      }

      if (result.toolName === "Sales Results") {
         summaryHeight = 18 + (2 * 20);
         if (SalesSearch.improvementType !== "Unimproved") {
            summaryHeight += 20; //Add space for bedroom, bathroom counts.
         }
      }

      //Initialize all field names if not specified in layer settings.
      displayFields = layerSpec.displayFields;

      if (displayFields.length === 0) {
         displayFields = [];
         for (const field of fs.fields) {
            displayFields.Add(field.name);
         }
      }

      const styles: Default.iStyles = {
         layName: { backgroundColor: Settings.primaryColor, color: 'white', fontWeight: 500, marginTop: 0, padding: 3, paddingLeft: 9 },

         expansionPanelMain: { backgroundColor: Settings.leftPanelExpansionColor },
         expansionPanelSummary: { minHeight: summaryHeight, height: summaryHeight, paddingLeft: 5, paddingRight: 15 }, //Must have padding right 15
         expansionPanelDetails: { padding: '1px 0px 9px 0px', backgroundColor: Settings.paperColor },

         row1Container: { maxWidth: (Settings.leftPanelWidth - 30), display: 'flex', flexWrap: 'wrap', justifyContent: 'space-around', alignItems: 'center' },

         table: { minWidth: Settings.leftPanelWidth - 30, fontWeight: 500, overflowX: 'auto', border: '1px solid silver', borderCollapse: 'collapse', borderSpacing: 0, fontFamily: 'Roboto, Helvetica, Arial, Verdana' },
         body: { cursor: 'default' },
      };

      //Add top margin between layer names.
      if (ii > 0) {
         styles.layName.marginTop = 9;
      }

      //Layer name heading.
      keyLayer = keyModule + "_" + layerName.replace(/ /g, '_');

      if (result.layerNameVisible) {
         jsxMain.push(
            <Typography key={keyLayer} variant="body1" style={styles.layName}>
               {layerName}
            </Typography>,
         );
      }

      if (!fs) {
         return jsxMain; //Quit if no data.
      }

      //Process all records.
      for (i = begRec; i <= endRec; i++) {
         feature = fs.features[i];
         keyFeature = keyLayer + "_" + i;

         //Get object id.
         let oid: number = 0;
         if (result.toolName !== "Data Table") {
            oid = Util_App.getObjectId(feature);
         }

         jsxMain.push(
            <Accordion key={keyFeature} TransitionProps={{ unmountOnExit: true }} defaultExpanded={expanded} style={styles.expansionPanelMain}
               onMouseOver={() => mouseOverOut(true, oid, fs.geometryType)} onMouseOut={() => mouseOverOut(false, oid, fs.geometryType)}>
               <AccordionSummary expandIcon={<Icon>arrow_drop_down</Icon>} style={styles.expansionPanelSummary}>
                  {getExpansionHeading(i, fs, feature, layerName, result)}
               </AccordionSummary>

               <AccordionDetails style={styles.expansionPanelDetails}>
                  <div>
                     <div style={styles.row1Container}>
                        {getTopLinks(fs, feature, layerName)}
                     </div>

                     <table style={styles.table}>
                        <tbody style={styles.body}>
                           {getTableRows(fs, feature, layerName, layerSpec)}
                        </tbody>
                     </table>
                  </div>
               </AccordionDetails>
            </Accordion>,
         );
      }

      return jsxMain;
   }

   //Mouse over and out of left panel results item.
   function mouseOverOut(over: boolean, oid: number, geomType: string) {
      let graLay: __esri.GraphicsLayer;

      if (DrawerMenu.curToolName === "Search Results" && oid > 0) {
         GraLay.clearSelectHoverGraphics();

         if (geomType === "point") {
            graLay = GraLay.selectedPoint;
         } else if (geomType === "polyline") {
            graLay = GraLay.selectedLine;
         } else { //polygon.
            graLay = GraLay.selectedPoly;
         }

         const gra = graLay.graphics.find(function (graphic) {
            return Util_App.getObjectId(graphic) === oid;
         });

         if (over) {
            Util_App.highlightGraphic(gra);
         }
      }
   }

   //Get expansion panel heading.
   function getExpansionHeading(ii: number, fs: __esri.FeatureSet, feature: Graphic, layerName: string, result: iResult): JSX.Element[] {
      const jsx: JSX.Element[] = [];
      const jj: number = Search.activeLayerIndex;
      const selectButtonTooltip: string = "Select or Remove this record";
      selectButtonVisible = false;
      salesCheckboxVisible = false;

      if (result.toolName === "Search Results" && Search.layers.length > 0) {
         if (Search.layers[jj].IDs.length > 1) {
            selectButtonVisible = true;
         }
      }

      if (result.toolName === "Sales Results" && SalesSearch.layer.totRec > 1) {
         salesCheckboxVisible = true;
      }

      let zoomToVisible: boolean = false;
      if (feature.geometry) {
         zoomToVisible = true;
      }

      const styles: Default.iStyles = {
         col1Container: { width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', alignItems: 'flex-start' },
         button: { minWidth: 30, paddingLeft: 3, paddingRight: 3, marginRight: 7 },
      };

      key = keyFeature + "_expansionHeading";

      if (feature) {
         jsx.push(
            <div key={key} style={styles.col1Container}>

               <div className="app-flex-row-between" style={{ width: '100%' }}>
                  <div className="app-flex-row-start" style={{ fontWeight: 500 }}>
                     {selectButtonVisible && <Button style={styles.button} variant="outlined" size="medium" color="primary" onClick={(event) => selectOne(ii, event)} title={selectButtonTooltip} aria-label={(ii + 1).toString()}>
                        {ii + 1}
                     </Button>}

                     {salesCheckboxVisible && <CheckboxSale.Render feature={feature} ii={ ii } />}

                     <div>
                        {getHeadingRows(fs, feature, layerName, result)}
                     </div>
                  </div>

                  {zoomToVisible && <div>
                     <IconButton style={{ paddingLeft: 5, paddingRight: 5 }} color="default" onClick={(event) => zoomTo(feature.geometry, event)} title="Zoom to" aria-label="Zoom to">
                        <Icon color="action">zoom_in</Icon>
                     </IconButton>
                  </div>}
               </div>

               <div className="app-flex-row-around">
                  {getPictureIBM(fs, feature)}
                  {getButtonsIBM(fs, feature, ii, layerName)}
               </div>
            </div>,
         );

      } else {
         jsx.push(<div key={key} className="app-flex-row-between" style={{ width: '100%' }}>
            Feature not available
         </div>,
         );
      }

      return jsx;
   }

   //Get heading rows.
   function getHeadingRows(fs: __esri.FeatureSet, feature: Graphic, layerName: string, result: iResult): JSX.Element[] {
      const jsx: JSX.Element[] = [];
      let i: number;
      let field: Util_App.iField;
      let field2: Util_App.iField;
      const fields: Util_App.iField[] = Util_App.getHeadingValues(fs, feature, layerName);
      let headWidth: number = 230;

      if (selectButtonVisible || salesCheckboxVisible) {
         headWidth -= 40;
      }

      const styles: Default.iStyles = {
         rowStyle1: { lineHeight: '1.35rem', fontWeight: 500, color: 'DarkRed', maxWidth: headWidth },
         rowStyle2: { lineHeight: '1.35rem', fontWeight: 500, maxWidth: headWidth },
         rowStyle3: { lineHeight: '1.35rem', fontWeight: 500, color: 'DarkGreen' },
      };

      //Sales search. Format: SiteAddress, 1-SalePrice, 2-SaleDate, 3-SqFt, 4-Area_SqFt, 5-Bedrooms, 6-Bathrooms
      if (result.toolName === "Sales Results") {
         key = keyFeature + "_headingRow_1";
         field = fields[0]; //Address.
         jsx.push(
            <Typography key={key} style={styles.rowStyle1} variant="body1">{field.value}</Typography>,
         );

         if (fields.length > 2) {
            key = keyFeature + "_headingRow_2";
            field = fields[1];  //Sale price.
            field2 = fields[2]; //Sale date.
            jsx.push(
               <Typography key={key} style={styles.rowStyle2} variant="body1">{field.value}&nbsp;&nbsp;&nbsp;{field2.value}</Typography>,
            );
         }

         if (SalesSearch.improvementType !== "Unimproved") {
            let html1: string = "";
            let html2: string = "";
            let html3: string = "";

            if (fields.length > 6) {
               field = fields[5]; //Bedrooms.
               if (field.value !== "" && field.value !== 0 && field.value !== "0") {
                  html1 = field.value + " bd";
               }

               field = fields[6]; //Bathrooms.
               if (field.value !== "" && field.value !== 0 && field.value !== "0") {
                  html2 = field.value + " ba";
               }
            }

            if (fields.length > 4) {
               field = fields[3];  //Sq ft.
               field2 = fields[4]; //Acres.

               if (field.value !== "" && field.value !== 0 && field.value !== "0") {
                  html3 = field.value + " sqft";

               } else if (field2.value !== "" && field2.value !== 0 && field2.value !== "0") {
                  html3 = field2.value + " acres";
               }
            }

            key = keyFeature + "_headingRow_3";
            jsx.push(
               <Typography key={key} style={styles.rowStyle3} variant="body1">{html1}&nbsp;&nbsp;{html2}&nbsp;&nbsp;{html3}</Typography>,
            );
         }

      } else { //All other results.
         let rowStyle = styles.rowStyle1;

         for (i = 0; i < fields.length; i++) {
            field = fields[i];

            if (i > 0) {
               rowStyle = styles.rowStyle2;
            }

            let titleTip: string = null;
            const fieldValue = field.value as string;
            if (fieldValue.length > 22) {
               titleTip = fieldValue;
            }

            key = keyFeature + "_headingRow_" + i;

            if (Settings.security.accessLevel >= field.accessLevel) {
               jsx.push(
                  <Typography key={key} style={rowStyle} variant="body1" title={titleTip} noWrap>{field.value}</Typography>,
               );
            }
         }
      }

      return jsx;
   }

   //Get picture image.
   function getPictureIBM(fs: any, feature: any): any[] {
      const jsx: any[] = [];

      if (Settings.security.accessLevel === 10) {
         const pictureLinks: string = Util_App.getFieldValueNoCase("PictureLinks", feature, fs);

         if (pictureLinks) {
            const links: string[] = pictureLinks.split(",");
            if (links) {
               const url: string = Settings.webServerURL + links[0];
               key = keyFeature + "_pictureIBM";

               jsx.push(
                  <div key={key}>
                     <Tooltip enterDelay={300} leaveDelay={200} placement="right-start"
                        title={
                           <React.Fragment>
                              <img src={url} width="480px" />
                           </React.Fragment>
                        }
                     >
                        <img src={url} height="100px" onClick={photoClick.bind(null, url)} style={{ cursor: "pointer" }} />
                     </Tooltip>
                  </div>,
               );
            }
         }
      }

      return jsx;
   }

   //Get IBM heading row.
   function getButtonsIBM(fs: any, feature: any, ii: number, layerName: string): any[] {
      const jsx: any[] = [];
      const mhYesNo: string = Util_App.getFieldValueNoCase("MobileHome", feature, fs);

      let mhVisible: boolean = false;
      if (mhYesNo === "yes") {
         mhVisible = true;
      }

      //Add edit buttons.
      if (layerName === Settings.parcelLayerName && Settings.security.accessLevel === 10) {
         key = keyFeature + "_buttonsIBM";

         jsx.push(
            <div key={key} className="app-flex-column-around" style={{ marginLeft: 9 }}>
               {getMenuButtonIBM("Edit", ii)}
               {mhVisible && getMenuButtonIBM("Mobile Homes", ii)}
            </div>,
         );
      }

      return jsx;
   }

   //Build IBM menu button.
   function getMenuButtonIBM(toolName: string, ii: number): any[] {
      const jsx: any[] = [];

      const styles2: any = {
         button: { marginTop: 0, marginBottom: 4 },
      };

      const ti: Settings.iToolInfo = Settings.getToolInfo(toolName);
      key = keyFeature + "_menuButtonIBM";

      jsx.push(
         <Button key={key} style={styles2.button} variant="contained" size="medium" color="primary" onClick={handleClick.bind(null, toolName, ii)} title={ti.tip} aria-label={toolName}>
            <Icon color="action">{ti.iconName}</Icon>
            <span style={{ marginLeft: 4 }}></span>
            {ti.label}
         </Button>,
      );
      return jsx;
   }

   //Photo click event.
   function photoClick(url: string, event: any) {
      window.open(url, "Picture");
      event.stopPropagation();
   }

   //IBM button click event.
   function handleClick(tool: any, ii?: number) {
      const jj: number = Search.activeLayerIndex;
      EditValuesIBM.setActivePIN(Search.layers[jj].IDs[ii].toString());
      DrawerMenu.showTool(tool);  //Calls EditValuesIBM, EditAdjustIBM, and MobileHomesIBM.
   }

   //Select one feature.
   function selectOne(ii: number, event: React.MouseEvent<HTMLButtonElement>) {
      if (SelectionMode.value === "Add") {
         event.stopPropagation();
         return;
      }
      const jj: number = Search.activeLayerIndex;
      const id = Search.layers[jj].IDs[ii];
      GetSearchResults.init(id);
      event.stopPropagation();
   }

   //Zoom to feature.
   function zoomTo(geom: __esri.Geometry, event: React.MouseEvent<HTMLButtonElement>) {
      let mbr = geom.extent;
      if (!mbr) {
         const point = geom as __esri.Point;
         mbr = new Extent({ xmin: point.x, ymin: point.y, xmax: point.x, ymax: point.y, spatialReference: MainMap.activeView.spatialReference });
      }
      mbr = Util_App.addMarginToMBR(mbr, true);

      MainMap.activeView.goTo(mbr, { duration: 1000 });
      event.stopPropagation();
   }

   //Get top hyperlinks. backgroundColor: Settings.leftPanelExpansionColor
   function getTopLinks(fs: __esri.FeatureSet, feature: Graphic, layerName: string): JSX.Element[] {
      const styles: Default.iStyles = {
         hyperlink: { lineHeight: '1.15rem', margin: 4, marginTop: 3, marginBottom: 5, fontSize: Settings.detailsFontSize, fontWeight: 500, fontFamily: 'Roboto, Helvetica, Arial, Verdana' },
      };

      const jsx: JSX.Element[] = [];
      const custom = Custom.getCustomLinks(layerName, feature, fs);

      for (let i: number = 0; i < custom.values.length; i++) {
         const value = custom.values[i];
         const url: string = custom.urls[i];
         const target: string = "W" + value.replace(/ /g, '_');

         key = keyFeature + "_topLinks_" + i;

         if (url && url.StartsWith("js:")) {
            jsx.push(
               <a key={key} style={styles.hyperlink} href="#" onClick={() => Custom.callFunction(url)}>{value}</a>,
            );

         } else {
            //Hack to not encode URL.
            if (url.Contains("Custom!")) {
               const html: string = '<a style="font-size: 0.95rem; font-weight: 500" href="' + url + '" target="Custom!">' + value + '</a>';
               jsx.push(
                  <div key={key} dangerouslySetInnerHTML={{ __html: html }} />,
               );

            } else {
               jsx.push(
                  <a key={key} style={styles.hyperlink} href={url} target={target}>{value}</a>,
               );
            }
         }
      }

      return jsx;
   }

   //Get table rows.
   function getTableRows(fs: __esri.FeatureSet, feature: Graphic, layerName: string, layerSpec: Settings.iLayer): JSX.Element[] {
      const jsx: JSX.Element[] = [];
      let field: Util_App.iField;

      const styles: Default.iStyles = {
         bodyCellLabel: { lineHeight: '1.15rem', padding: 3, backgroundColor: Settings.leftPanelLabelColor, color: 'black', fontSize: Settings.detailsFontSize, borderTop: '1px solid silver' },
         bodyCellValue: { lineHeight: '1.15rem', padding: 3, textAlign: 'left', paddingLeft: 5, color: 'black', fontSize: Settings.detailsFontSize, borderTop: '1px solid silver' },
      };

      let i: number;
      const fields: Util_App.iField[] = Util_App.getFeatureValues(fs, feature, layerName);

      for (const fieldName of displayFields) {
         i = Util_App.getFieldIndexByName(fieldName, fs);

         if (i >= 0) {
            field = fields[i];
            keyRow = keyFeature + "_row_" + i;

            if (Settings.security.accessLevel >= field.accessLevel) {
               jsx.push(
                  <tr key={keyRow} tabIndex={-1}>
                     <td style={styles.bodyCellLabel}>{field.alias}</td>
                     <td style={styles.bodyCellValue}>{getTableRowValue(field)}</td>
                  </tr>,
               );
            }

         } else {
            Util.errMsg("Field name not found in feature set: " + fieldName);
         }
      }
      return jsx;
   }

   //Get table row value.
   function getTableRowValue(field: Util_App.iField): JSX.Element[] {
      const jsx: JSX.Element[] = [];

      if (field.url === "") {
         key = keyRow + "_fieldValue";

         jsx.push(
            <span key={key}>{field.value}</span>,
         );

      } else {
         key = keyRow + "_url";

         if (field.url && field.url.StartsWith("js:")) {
            jsx.push(
               <a key={key} href="#" onClick={() => Custom.callFunction(field.url)}>{field.value}</a>,
            );

         } else {
            //Hack to not encode URL.
            if (field.url.Contains("Custom!")) {
               const html: string = '<a href="' + field.url + '" target="_blank">' + field.value + '</a>';
               jsx.push(
                  <div key={key} dangerouslySetInnerHTML={{ __html: html }} />,
               );

            } else {
               jsx.push(
                  <a key={key} href={field.url} target="_blank" aria-label={field.value as string}>{field.value}</a>,
               );
            }
         }
      }

      return jsx;
   }

   //Render the results.
   Util.logMsg("Rendering ResultsFS");

   return (
      <Paper key={Util.uid()}>
         {props.results.map(function (result: iResult, i: number) {
            return (getExpansionPanel(result, i));
         })}
      </Paper>
   ); //end return.

} //end function.
